/**
 * Knowledge list state machine - replaces useKnowledgeStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type {
  KnowledgeArticle,
  KnowledgeCategory,
  KnowledgeFAQ,
  KnowledgeSearchResult,
  KnowledgeFilters,
  KnowledgeStats,
} from '../types'
import type { ArticleFeedbackParams } from '../types'

function computeSearchResults(
  query: string,
  articles: KnowledgeArticle[],
  faqs: KnowledgeFAQ[]
): KnowledgeSearchResult[] {
  const lowerQuery = query.toLowerCase()
  const results: KnowledgeSearchResult[] = []
  articles.forEach((article) => {
    if (
      article.title.toLowerCase().includes(lowerQuery) ||
      article.content.toLowerCase().includes(lowerQuery) ||
      article.tags.some((t) => t.toLowerCase().includes(lowerQuery))
    ) {
      results.push({
        id: article.id,
        type: 'article',
        title: article.title,
        excerpt: article.excerpt || article.content.substring(0, 150),
        categoryId: article.categoryId,
        categoryName: article.categoryName,
        relevanceScore: article.title.toLowerCase().includes(lowerQuery) ? 1 : 0.5,
        highlights: [],
      })
    }
  })
  faqs.forEach((faq) => {
    if (
      faq.question.toLowerCase().includes(lowerQuery) ||
      faq.answer.toLowerCase().includes(lowerQuery)
    ) {
      results.push({
        id: faq.id,
        type: 'faq',
        title: faq.question,
        excerpt: faq.answer.substring(0, 150),
        categoryId: faq.categoryId,
        categoryName: faq.categoryName,
        relevanceScore: faq.question.toLowerCase().includes(lowerQuery) ? 1 : 0.5,
        highlights: [],
      })
    }
  })
  results.sort((a, b) => b.relevanceScore - a.relevanceScore)
  return results
}

function computeStats(
  articles: KnowledgeArticle[],
  faqs: KnowledgeFAQ[],
  categories: KnowledgeCategory[]
): KnowledgeStats {
  const totalHelpful = articles.reduce((sum, a) => sum + a.helpfulCount, 0)
  const totalNotHelpful = articles.reduce((sum, a) => sum + a.notHelpfulCount, 0)
  return {
    totalArticles: articles.length,
    publishedArticles: articles.filter((a) => a.status === 'published').length,
    draftArticles: articles.filter((a) => a.status === 'draft').length,
    totalFAQs: faqs.length,
    totalCategories: categories.length,
    totalViews: articles.reduce((sum, a) => sum + a.viewCount, 0),
    totalHelpful,
    totalNotHelpful,
    helpfulRate:
      totalHelpful + totalNotHelpful > 0
        ? (totalHelpful / (totalHelpful + totalNotHelpful)) * 100
        : 0,
    popularArticles: [...articles]
      .sort((a, b) => b.viewCount - a.viewCount)
      .slice(0, 5)
      .map((a) => ({ id: a.id, title: a.title, viewCount: a.viewCount })),
    recentArticles: [...articles]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)
      .map((a) => ({ id: a.id, title: a.title, createdAt: a.createdAt })),
    articlesByCategory: categories.map((c) => ({
      categoryId: c.id,
      categoryName: c.name,
      count: articles.filter((a) => a.categoryId === c.id).length,
    })),
  }
}

export interface KnowledgeListContext {
  categories: KnowledgeCategory[]
  articles: KnowledgeArticle[]
  faqs: KnowledgeFAQ[]
  searchResults: KnowledgeSearchResult[]
  stats: KnowledgeStats | null
  selectedCategoryId: string | null
  selectedArticleId: string | null
  filters: KnowledgeFilters
  isLoading: boolean
  isSearching: boolean
  error: string | null
}

export type KnowledgeListEvent =
  | { type: 'SET_CATEGORIES'; categories: KnowledgeCategory[] }
  | { type: 'ADD_CATEGORY'; category: KnowledgeCategory }
  | { type: 'UPDATE_CATEGORY'; id: string; updates: Partial<KnowledgeCategory> }
  | { type: 'REMOVE_CATEGORY'; id: string }
  | { type: 'SET_ARTICLES'; articles: KnowledgeArticle[] }
  | { type: 'ADD_ARTICLE'; article: KnowledgeArticle }
  | { type: 'UPDATE_ARTICLE'; id: string; updates: Partial<KnowledgeArticle> }
  | { type: 'REMOVE_ARTICLE'; id: string }
  | { type: 'INCREMENT_VIEW_COUNT'; id: string }
  | { type: 'SUBMIT_FEEDBACK'; params: ArticleFeedbackParams }
  | { type: 'SET_FAQS'; faqs: KnowledgeFAQ[] }
  | { type: 'ADD_FAQ'; faq: KnowledgeFAQ }
  | { type: 'UPDATE_FAQ'; id: string; updates: Partial<KnowledgeFAQ> }
  | { type: 'REMOVE_FAQ'; id: string }
  | { type: 'SEARCH'; query: string }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'SET_SELECTED_CATEGORY'; id: string | null }
  | { type: 'SET_SELECTED_ARTICLE'; id: string | null }
  | { type: 'SET_FILTERS'; filters: Partial<KnowledgeFilters> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_STATS'; stats: KnowledgeStats }
  | { type: 'REFRESH_STATS' }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as KnowledgeListContext,
    events: {} as KnowledgeListEvent,
  },
})

export const knowledgeListMachine = machineSetup.createMachine({
  id: 'knowledgeList',
  context: {
    categories: [],
    articles: [],
    faqs: [],
    searchResults: [],
    stats: null,
    selectedCategoryId: null,
    selectedArticleId: null,
    filters: {},
    isLoading: false,
    isSearching: false,
    error: null,
  },
  on: {
    SET_CATEGORIES: {
      actions: assign({
        categories: ({ event }) => (event.type === 'SET_CATEGORIES' ? event.categories : []),
      }),
    },
    ADD_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) =>
          event.type === 'ADD_CATEGORY'
            ? [...context.categories, event.category]
            : context.categories,
      }),
    },
    UPDATE_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) => {
          if (event.type !== 'UPDATE_CATEGORY') return context.categories
          return context.categories.map((c) => (c.id === event.id ? { ...c, ...event.updates } : c))
        },
      }),
    },
    REMOVE_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) => {
          if (event.type !== 'REMOVE_CATEGORY') return context.categories
          return context.categories.filter((c) => c.id !== event.id)
        },
      }),
    },
    SET_ARTICLES: {
      actions: assign({
        articles: ({ event }) => (event.type === 'SET_ARTICLES' ? event.articles : []),
      }),
    },
    ADD_ARTICLE: {
      actions: assign({
        articles: ({ context, event }) =>
          event.type === 'ADD_ARTICLE' ? [...context.articles, event.article] : context.articles,
      }),
    },
    UPDATE_ARTICLE: {
      actions: assign({
        articles: ({ context, event }) => {
          if (event.type !== 'UPDATE_ARTICLE') return context.articles
          return context.articles.map((a) => (a.id === event.id ? { ...a, ...event.updates } : a))
        },
      }),
    },
    REMOVE_ARTICLE: {
      actions: assign({
        articles: ({ context, event }) => {
          if (event.type !== 'REMOVE_ARTICLE') return context.articles
          return context.articles.filter((a) => a.id !== event.id)
        },
      }),
    },
    INCREMENT_VIEW_COUNT: {
      actions: assign({
        articles: ({ context, event }) => {
          if (event.type !== 'INCREMENT_VIEW_COUNT') return context.articles
          return context.articles.map((a) =>
            a.id === event.id ? { ...a, viewCount: a.viewCount + 1 } : a
          )
        },
      }),
    },
    SUBMIT_FEEDBACK: {
      actions: assign({
        articles: ({ context, event }) => {
          if (event.type !== 'SUBMIT_FEEDBACK') return context.articles
          const { articleId, isHelpful } = event.params
          return context.articles.map((a) =>
            a.id === articleId
              ? isHelpful
                ? { ...a, helpfulCount: a.helpfulCount + 1 }
                : { ...a, notHelpfulCount: a.notHelpfulCount + 1 }
              : a
          )
        },
      }),
    },
    SET_FAQS: {
      actions: assign({
        faqs: ({ event }) => (event.type === 'SET_FAQS' ? event.faqs : []),
      }),
    },
    ADD_FAQ: {
      actions: assign({
        faqs: ({ context, event }) =>
          event.type === 'ADD_FAQ' ? [...context.faqs, event.faq] : context.faqs,
      }),
    },
    UPDATE_FAQ: {
      actions: assign({
        faqs: ({ context, event }) => {
          if (event.type !== 'UPDATE_FAQ') return context.faqs
          return context.faqs.map((f) => (f.id === event.id ? { ...f, ...event.updates } : f))
        },
      }),
    },
    REMOVE_FAQ: {
      actions: assign({
        faqs: ({ context, event }) => {
          if (event.type !== 'REMOVE_FAQ') return context.faqs
          return context.faqs.filter((f) => f.id !== event.id)
        },
      }),
    },
    SEARCH: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'SEARCH') return {}
        const results = computeSearchResults(event.query, context.articles, context.faqs)
        return { searchResults: results, isSearching: false }
      }),
    },
    CLEAR_SEARCH: {
      actions: assign({ searchResults: [] }),
    },
    SET_SELECTED_CATEGORY: {
      actions: assign({
        selectedCategoryId: ({ event }) =>
          event.type === 'SET_SELECTED_CATEGORY' ? event.id : null,
      }),
    },
    SET_SELECTED_ARTICLE: {
      actions: assign({
        selectedArticleId: ({ event }) => (event.type === 'SET_SELECTED_ARTICLE' ? event.id : null),
      }),
    },
    SET_FILTERS: {
      actions: assign({
        filters: ({ context, event }) => {
          if (event.type !== 'SET_FILTERS') return context.filters
          return { ...context.filters, ...event.filters }
        },
      }),
    },
    RESET_FILTERS: {
      actions: assign({ filters: {} }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    SET_ERROR: {
      actions: assign({
        error: ({ event }) => (event.type === 'SET_ERROR' ? event.error : null),
      }),
    },
    SET_STATS: {
      actions: assign({
        stats: ({ event }) => (event.type === 'SET_STATS' ? event.stats : null),
      }),
    },
    REFRESH_STATS: {
      actions: assign(({ context }) => {
        const stats = computeStats(context.articles, context.faqs, context.categories)
        return { stats }
      }),
    },
    CLEAR: {
      actions: assign({
        categories: [],
        articles: [],
        faqs: [],
        searchResults: [],
        stats: null,
        selectedCategoryId: null,
        selectedArticleId: null,
        filters: {},
        isLoading: false,
        isSearching: false,
        error: null,
      }),
    },
  },
})
